import React, { useCallback } from 'react';
import { FieldErrors, useForm, UseFormProps, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  ConfigureFieldInterface,
  DataTypeOfZodSchema,
  KeysOfZodSchema,
  ValidFormSubmitHandler,
  ZodSchema,
  ZodSchemaOrEffects,
} from '../types';

import { getRequired } from './getRequired';

/** Arguments for useKibbleForm hook with schema-specific type safety */
export type UseKibbleFormArgs<Z extends ZodSchemaOrEffects> = Partial<
  Omit<UseFormProps<DataTypeOfZodSchema<Z>>, 'resolver'>
> & {
  /** Zod schema  */
  schema: Z;
  /** Handler called on successful validation */
  onValidSubmit?: ValidFormSubmitHandler<Z>;
  /** Handler called on validation failure */
  onInvalidSubmit?: (errors: FieldErrors<DataTypeOfZodSchema<Z>>) => void;
  /** Options for submit button behavior during form submission */
  duringSubmit?: {
    /** Show loading spinner on submit button during submission */
    showSubmitButtonLoadingSpinner?: boolean;
    /** Disable submit button during submission */
    disableSubmitButton?: boolean;
  };
  /** External control of submitting state for async operations */
  isSubmitting?: boolean;
  /** UNSAFE: Internal overrides for testing */
  UNSAFE_overrides?: {
    /** Override default 1000ms form cooldown period */
    formCooldownMs?: number;
  };
};

/** Return type of useKibbleForm hook with enhanced form utilities */
export type UseKibbleFormReturn<Z extends ZodSchemaOrEffects> = Omit<
  UseFormReturn<DataTypeOfZodSchema<Z>>,
  'register'
> & {
  /** Configure a field for use with Kibble form components */
  configureField: (key: KeysOfZodSchema<Z>) => ConfigureFieldInterface<Z>;
  /** Props to spread onto form element */
  formProps: {
    onSubmit: React.FormEventHandler<HTMLFormElement>;
  };
  /** Props to spread onto submit button */
  submitButtonProps: {
    type: 'submit';
    onPress: (events: { webEvent?: React.MouseEvent<HTMLElement>; rxnEvent?: unknown }) => unknown;
    disabled?: boolean;
    loading?: boolean;
  };
};

/** Amount of time to prevent submission of ANY useKibbleForm() after submit.
 * The only situation where we want to allow rapid-fire form submissions is during unit tests. */
const FORM_COOLDOWN_MS = process.env.JEST_WORKER_ID ? 0 : 1000;
/** Global boolean used to prevent duplicate form submissions.
 * We use this sync global because it is VERY fast, much faster than setting React state. */
let isFormInCooldown = false;

/**
 * Enhanced wrapper around react-hook-form's useForm hook.
 * Provides Zod schema validation, type safety, duplicate submission prevention,
 * and utilities for configuring Kibble form fields.
 *
 * Usage guide: `kibble/forms/Overview.md`.
 */
const useKibbleForm = <Z extends ZodSchemaOrEffects>(
  args: UseKibbleFormArgs<Z>
): UseKibbleFormReturn<Z> => {
  const {
    schema,
    onValidSubmit = () => {},
    onInvalidSubmit = () => {},
    isSubmitting: isSubmittingProp,
    duringSubmit = {},
    UNSAFE_overrides = {}, // eslint-disable-line camelcase
    ...rest
  } = args;

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  let zodObject: ZodSchema;
  // if the schema is a ZodEffect, pull out the underlying ZodObject
  // eslint-disable-next-line no-underscore-dangle
  if (schema._def.typeName === 'ZodEffects') {
    zodObject = schema._def.schema; // eslint-disable-line no-underscore-dangle
  } else {
    zodObject = schema as ZodSchema;
  }

  const { register, ...form } = useForm<DataTypeOfZodSchema<Z>>({
    ...rest,
    resolver: zodResolver(schema),
  });

  const handleValidSubmit = useCallback(
    (data: DataTypeOfZodSchema<Z>) => {
      if (isFormInCooldown) return; // prevent duplicate submissions in the cooldown period

      // start (and end) the cooldown period
      isFormInCooldown = true;
      setIsSubmitting(true);
      setTimeout(() => {
        isFormInCooldown = false;
        setIsSubmitting(false);
      }, UNSAFE_overrides.formCooldownMs || FORM_COOLDOWN_MS); // eslint-disable-line camelcase

      onValidSubmit(data, { setError: form.setError });
    },
    [onValidSubmit, form.setError, setIsSubmitting]
  );

  return {
    ...form,
    formProps: {
      onSubmit: form.handleSubmit(handleValidSubmit, onInvalidSubmit),
    },
    submitButtonProps: {
      type: 'submit',
      onPress: ({ webEvent }) => {
        const handler = form.handleSubmit(handleValidSubmit, onInvalidSubmit);
        handler(webEvent);
      },
      disabled: duringSubmit.disableSubmitButton ? isSubmittingProp || isSubmitting : undefined,
      loading: duringSubmit.showSubmitButtonLoadingSpinner
        ? isSubmittingProp || isSubmitting
        : undefined,
    },
    // use of ExtractSchemaKeys<Z> provides type safety for the key arg; it must be a key of the schema
    configureField: (key: KeysOfZodSchema<Z>) =>
      ({
        control: form.control,
        name: key,
        fieldProps: register(key),
        fieldError: form.getFieldState(key, form.formState).error,
        required: getRequired(zodObject, key),
      } as ConfigureFieldInterface<Z>), // return as the ConfigureFieldInterface for the specific schema
  };
};

export default useKibbleForm;
